import React, { useMemo } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import styled from 'styled-components';
import { ANALYTIC_PANEL_COLORS } from 'utils/constants/analyticPanelColors';
import { convertPrice } from 'utils/convertPrice';
import { formatShortCurrency } from 'utils/formatters/formatShortCurrency';
import { stringToNumberOrZero } from 'utils/formatters/stringToNumberOrZero';
import { ReportData, TimelineChartItem } from 'utils/types/AnalyticPanelReport';

import { PDF_EXPORT_CLASS } from './pdfExportClass';

const ReportSection = styled.div`
    background: var(--color-white);
    border-radius: var(--box-border-radius);
    padding: 24px;
    margin-bottom: 20px;

    .${PDF_EXPORT_CLASS} & {
        border: 1px solid var(--pdf-card-border);
    }
`;

const SectionTitle = styled.h2`
    font-size: 16px;
    font-weight: 600;
    color: var(--color-primary);
    margin-bottom: 16px;
`;

const ChartContainer = styled.div`
    width: 100%;
    height: 300px;
`;

const CapitalSummary = styled.div`
    margin-bottom: 16px;
    padding: 12px 16px;
    background: var(--color-alt-second);
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const CapitalSummaryValue = styled.strong`
    font-size: 18px;
    color: var(--color-primary);
`;

const tooltipContentStyle = {
    background: 'var(--color-white)',
    border: '1px solid var(--color-alt-second)',
    borderRadius: '8px'
};

const labelListStyle = { fontSize: 12, fontWeight: 600, fill: 'var(--color-primary)' };

interface TimelineCostsChartProps {
    data: ReportData;
    isAnimationActive?: boolean;
}

const TIMELINE_COLORS = [
    ANALYTIC_PANEL_COLORS.purchasePrice,
    ANALYTIC_PANEL_COLORS.financingCosts,
    ANALYTIC_PANEL_COLORS.renovation,
    ANALYTIC_PANEL_COLORS.saleCosts
];

export const TimelineCostsChart: React.FC<TimelineCostsChartProps> = ({ data, isAnimationActive = true }) => {
    const { flip } = data;

    const chartData: TimelineChartItem[] = useMemo(() => {
        if (!flip) return [];

        const items: TimelineChartItem[] = [
            { name: '1 etap', value: stringToNumberOrZero(flip.firstStage) },
            { name: '2 etap', value: stringToNumberOrZero(flip.secondStage) },
            { name: '3 etap', value: stringToNumberOrZero(flip.thirdStage) }
        ];

        if (stringToNumberOrZero(flip.finalCosts) > 0) {
            items.push({
                name: 'Koszty końcowe',
                value: stringToNumberOrZero(flip.finalCosts)
            });
        }

        return items;
    }, [flip]);

    const totalCapitalNeeded = chartData.reduce((sum, item) => sum + item.value, 0);

    if (chartData.length === 0) return null;

    return (
        <ReportSection>
            <SectionTitle>
                Koszty flipa w czasie / Zapotrzebowanie na kapitał
            </SectionTitle>

            <CapitalSummary>
                <span>Suma zapotrzebowania na kapitał:</span>
                <CapitalSummaryValue>{convertPrice(totalCapitalNeeded)} zł</CapitalSummaryValue>
            </CapitalSummary>

            <ChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        margin={{ top: 40, right: 30, left: 20, bottom: 20 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="var(--color-alt-second)"
                        />
                        <XAxis
                            dataKey="name"
                            tick={{ fontSize: 13, fill: 'var(--color-primary)' }}
                            tickLine={false}
                            axisLine={{ stroke: 'var(--color-alt-second)' }}
                        />
                        <YAxis
                            tick={{ fontSize: 12, fill: 'var(--color-primary-70)' }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={formatShortCurrency}
                        />
                        <Tooltip
                            formatter={(value: number) => [`${convertPrice(value)} zł`, 'Wartość']}
                            contentStyle={tooltipContentStyle}
                        />
                        <Bar
                            isAnimationActive={isAnimationActive}
                            dataKey="value"
                            radius={[
                                6,
                                6,
                                0,
                                0
                            ]}
                            maxBarSize={80}
                        >
                            {chartData.map((entry, index) => <Cell
                                key={`cell-${index}`}
                                fill={TIMELINE_COLORS[index % TIMELINE_COLORS.length]}
                            />)}
                            <LabelList
                                dataKey="value"
                                position="top"
                                formatter={formatShortCurrency}
                                style={labelListStyle}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </ChartContainer>
        </ReportSection>
    );
};
